/* Reference Sidebar (ported from vault-app), wired to our VaultCtx + real counts/storage. */
import type { ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { SPRING_INDICATOR, SPRING_PRESS } from "@/shared/motion/transitions";
import { useApp } from "./ctx";
import Ic from "./Ic";

export interface SidebarData {
  counts?: { shared?: number; links?: number; collections?: number; approvals?: number };
  storageText?: ReactNode;
  storagePct?: number; // 0..100
  connected?: boolean; // real S3 connection state (undefined = unknown → keep reference look)
}

export default function Sidebar({ data }: { data?: SidebarData }) {
  const { page, go, open, navOpen } = useApp();
  const reduce = useReducedMotion();
  const c = data?.counts ?? {};
  const connected = data?.connected;
  // Only badge a count we actually have (a real 0 shows "0"; an unknown/failed
  // count shows no badge — never a fabricated demo number).
  const countBadge = (n?: number, red = false) =>
    typeof n === "number"
      ? <span key="c" className="count" style={red ? { color: "var(--red)", fontWeight: 700 } : undefined}>{n}</span>
      : undefined;

  const NAV: [string, string, ReactNode, ReactNode?][] = [
    ["drive", "folder", "My Drive"],
    ["shared", "users", "Shared with me", countBadge(c.shared)],
    ["links", "link", "Share links", countBadge(c.links)],
    ["collections", "grid", "Collections", countBadge(c.collections)],
    ["playground", "sparkle", <span key="n" className="font-canvas" style={{ fontSize: 15 }}>Canvas</span>, <span key="c" className="badge gold" style={{ marginLeft: "auto", fontSize: 9 }}>LIVE</span>],
    ["bulkrender", "bolt", "Bulk Render"],
    ["approvals", "check", "Approvals", countBadge(c.approvals, (c.approvals ?? 0) > 0)],
    ["insights", "bars", "Insights"],
    ["recent", "clock", "Recent"],
    ["starred", "star", "Starred"],
    ["trash", "trash", "Trash"],
  ];
  const WORK: [string, string, ReactNode, ReactNode?][] = [
    ["ring", "gem", "Ring Builder"],
    ["render", "sliders", "Render settings"],
    ["branding", "palette", "Branding"],
    ["s3", "db", "Storage / S3", <span key="c" className="conn-dot" title={connected === false ? "Storage not connected" : "Connected"} style={{ marginLeft: "auto", ...(connected === false ? { background: "var(--muted)", boxShadow: "none" } : {}) }}></span>],
    ["team", "user", "Team"],
    ["billing", "card", "Plans & billing"],
  ];

  const active = (k: string) => page === k || (k === "drive" && page === "folder") || (k === "collections" && page === "collection1");
  // Active look moves to a shared-layoutId indicator that slides between items; the
  // button reproduces `.active`'s text colour/weight inline (the CSS `.active` bg is
  // now the sliding indicator instead — a static bg would kill the slide).
  const Item = ([key, ico, label, extra]: [string, string, ReactNode, ReactNode?]) => {
    const isActive = active(key);
    return (
      <motion.button
        key={key}
        className="nav-item"
        onClick={() => go(key)}
        style={isActive ? { color: "var(--accent-strong)", fontWeight: 600 } : undefined}
        whileHover={reduce ? undefined : { x: 2 }}
        transition={SPRING_PRESS}
      >
        {isActive && (
          <motion.div
            layoutId="nav-active"
            transition={reduce ? { duration: 0 } : SPRING_INDICATOR}
            style={{ position: "absolute", inset: 0, borderRadius: "inherit", background: "var(--accent-soft)", zIndex: -1 }}
          />
        )}
        <Ic n={ico} style={{ flex: "0 0 18px", opacity: isActive ? 1 : 0.75 }} />
        {label}{extra}
      </motion.button>
    );
  };

  return (
    <aside className={"sidebar" + (navOpen ? " open" : "")}>
      <button className="new-btn" onClick={() => open("upload")}><Ic n="plus" size={16} sw={2.2} /> New / Upload</button>
      {NAV.map(Item)}
      <div className="nav-label">Workspace</div>
      {WORK.map(Item)}
      <div className="storage">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 700 }}>Storage</span>
          <a href="#" onClick={(e) => { e.preventDefault(); go("billing"); }}>Upgrade</a>
        </div>
        <div className="bar"><div className="fill" style={{ width: `${data?.storagePct ?? 0}%` }}></div></div>
        <div className="txt">{data?.storageText ?? <>Storage · <span style={{ color: "var(--muted)" }}>usage unavailable</span></>}</div>
      </div>
    </aside>
  );
}
